import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomerServiceIcon,
  ExperimentIcon,
  ReadIcon,
  AdminIcon,
  UrlOpenIcon,
  ChevronRight,
  CostsIcon,
} from "../../../assets/icons";

import ChemicalLibraryDialog from "../../chemicalLibrary/ChemicalLibraryDialog";
import UserProfileItem from "./UserProfileItem";
import "./index.scss";

const UserProfile = ({
  userName,
  userEmail,
  userRole,
  onManageAccess,
  onManageUser,
  isPathIncluded,
  onAvatarHover,
  onUtilityCosts,
  isForcedOpen = false,
}) => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef(null);

  const [profileOpen, setProfileOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [chemicalLibraryOpen, setChemicalLibraryOpen] = useState(false);

  const userManualUrl = process.env.REACT_APP_USER_MANUAL_URL;
  const defaultContactUsUrl = process.env.REACT_APP_CONTACT_US_URL;
  const chineseContactUsUrl = process.env.REACT_APP_CONTACT_US_URL_CN;
  const activeLanguage = (
    i18n?.resolvedLanguage ||
    i18n?.language ||
    ""
  ).toLowerCase();
  const isChinese = activeLanguage.startsWith("zh");
  const contactUsUrl = isChinese ? chineseContactUsUrl : defaultContactUsUrl;

  const isAdmin = userRole === "admin";

  const initials = useMemo(() => {
    const parts = (userName || "")
      .split(" ")
      .map((namePart) => namePart.trim())
      .filter(Boolean);

    if (!parts.length) {
      return "U";
    }

    return parts
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join("");
  }, [userName]);

  const closeMenus = () => {
    if (isForcedOpen) {
      return;
    }
    setProfileOpen(false);
    setAdminMenuOpen(false);
  };

  useEffect(() => {
    setProfileOpen(isForcedOpen);
  }, [isForcedOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        closeMenus();
      }
    };

    if (profileOpen || adminMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileOpen, adminMenuOpen, isForcedOpen]);

  const handleProfileToggle = () => {
    setProfileOpen((prev) => {
      if (prev) {
        setAdminMenuOpen(false);
      }
      return !prev;
    });
  };

  const handleAdminSettingsMouseEnter = () => {
    setAdminMenuOpen(true);
  };

  const handleAdminSettingsMouseLeave = () => {
    setAdminMenuOpen(false);
  };

  const handleManageAccessClick = () => {
    onManageAccess?.();
    closeMenus();
  };

  const handleManageUserClick = () => {
    onManageUser?.();
    closeMenus();
  };

  const handleUtilityCostsClick = () => {
    onUtilityCosts?.();
    closeMenus();
  };

  const handleExternalLinkClick = (url) => {
    if (!url) {
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
    closeMenus();
  };

  const handleChemicalLibraryClick = () => {
    setChemicalLibraryOpen(true);
    closeMenus();
  };

  const handleChemicalLibraryClose = () => {
    setChemicalLibraryOpen(false);
  };

  return (
    <div
      id="user-profile"
      className="user-profile-container"
      ref={containerRef}
    >
      <div
        id="user-profile-avatar"
        className="user-profile-avatar pointer"
        onMouseEnter={onAvatarHover}
        onClick={handleProfileToggle}
      >
        {initials}
      </div>

      {profileOpen && (
        <div className="user-profile-dropdown">
          <div className="user-profile-summary">
            <div className="user-profile-avatar">{initials}</div>
            <div className="user-profile-summary-content">
              <span className="user-profile-name">
                {userName || t("userProfile.userFallback")}
              </span>
              <span className="user-profile-email">{userEmail}</span>
            </div>
          </div>

          <div className="user-profile-divider" />

          <UserProfileItem
            id="user-chemical-library"
            icon={ExperimentIcon}
            label={t("userProfile.chemicalLibrary")}
            onClick={handleChemicalLibraryClick}
          />
          <UserProfileItem
            id="user-utility-costs"
            icon={CostsIcon}
            label={t("userProfile.utilityCosts")}
            onClick={handleUtilityCostsClick}
          />
          {/* <UserProfileItem
            icon={LocaleIcon}
            label={t("userProfile.currencyAndUnits")}
          /> */}

          {isAdmin && (
            <div
              className={"user-profile-admin-settings"}
              onMouseEnter={handleAdminSettingsMouseEnter}
              onMouseLeave={handleAdminSettingsMouseLeave}
            >
              <UserProfileItem
                icon={AdminIcon}
                label={t("userProfile.adminSettings")}
                trailingIcon={ChevronRight}
                className="clickable"
              />

              {adminMenuOpen && (
                <div className="admin-menu-dropdown user-profile-admin-menu">
                  <div
                    className={`admin-menu-item ${isPathIncluded("admin-portal") ? "disabled" : ""}`}
                    onClick={handleManageAccessClick}
                  >
                    {t("adminPage.manageAcess")}
                  </div>
                  <div
                    className={`admin-menu-item ${isPathIncluded("user-management") ? "disabled" : ""}`}
                    onClick={handleManageUserClick}
                  >
                    {t("adminPage.userManagement")}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="user-profile-divider" />

          <UserProfileItem
            icon={ReadIcon}
            label={t("header.userManual")}
            trailingIcon={UrlOpenIcon}
            onClick={() => handleExternalLinkClick(userManualUrl)}
          />

          <UserProfileItem
            icon={CustomerServiceIcon}
            label={t("userProfile.contactSupport")}
            trailingIcon={UrlOpenIcon}
            onClick={() => handleExternalLinkClick(contactUsUrl)}
          />
        </div>
      )}

      {chemicalLibraryOpen && (
        <ChemicalLibraryDialog onClose={handleChemicalLibraryClose} />
      )}
    </div>
  );
};

export default UserProfile;
